import React from "react";
import { useNavigate } from "react-router-dom";

interface ForeignStaffBasicInfoProps {
  formData: {
    fullName: string;
    dateOfBirth: string;
    gender: string;
    phone: string;
    qualification: string;
    academicRank: string;
    degree: string;
    role: string;
    email: string;
    passportNumber: string;
    passportExpiry: string;
    visaExpiry: string;
    entryDate: string;
    temporaryAddress: string;
    nationality: string;
    country: string;
    organization: string;
  };
  handleInputChange: (field: string, value: string) => void;
}

const ForeignStaffBasicInfo: React.FC<ForeignStaffBasicInfoProps> = ({
  formData,
  handleInputChange,
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      {/* First Row - Five Columns: Họ và tên (*), Ngày sinh, Giới tính, Điện thoại (*), Email */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        {/* Họ và tên (*) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Họ và tên <span className="text-red-500">(*)</span>
          </label>
          <input
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange("fullName", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Họ và tên"
          />
        </div>

        {/* Ngày sinh */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ngày sinh
          </label>
          <input
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Giới tính */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Giới tính
          </label>
          <select
            value={formData.gender}
            onChange={(e) => handleInputChange("gender", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Giới tính -</option>
            <option value="male">Nam</option>
            <option value="female">Nữ</option>
            <option value="other">Khác</option>
          </select>
        </div>

        {/* Điện thoại (*) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Điện thoại <span className="text-red-500">(*)</span>
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Điện thoại..."
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Email..."
          />
        </div>
      </div>

      {/* Second Row - Four Columns: Trình độ, Học vị, Vai trò, Học hàm */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        {/* Trình độ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trình độ
          </label>
          <select
            value={formData.qualification}
            onChange={(e) => handleInputChange("qualification", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Trình độ -</option>
            <option value="bachelor">Cử nhân</option>
            <option value="master">Thạc sĩ</option>
            <option value="phd">Tiến sĩ</option>
            <option value="professor">Giáo sư</option>
          </select>
        </div>

        {/* Học vị */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Học vị
          </label>
          <select
            value={formData.degree}
            onChange={(e) => handleInputChange("degree", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Học vị -</option>
            <option value="bachelor">Cử nhân</option>
            <option value="master">Thạc sĩ</option>
            <option value="phd">Tiến sĩ</option>
            <option value="postdoc">Nghiên cứu sinh</option>
          </select>
        </div>

        {/* Vai trò */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vai trò
          </label>
          <select
            value={formData.role}
            onChange={(e) => handleInputChange("role", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Vai trò -</option>
            <option value="researcher">Nghiên cứu viên</option>
            <option value="lecturer">Giảng viên</option>
            <option value="consultant">Tư vấn</option>
            <option value="expert">Chuyên gia</option>
            <option value="visiting-scholar">Học giả thỉnh giảng</option>
          </select>
        </div>

        {/* Học hàm */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Học hàm
          </label>
          <select
            value={formData.academicRank}
            onChange={(e) => handleInputChange("academicRank", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Học hàm -</option>
            <option value="assistant">Trợ lý</option>
            <option value="lecturer">Giảng viên</option>
            <option value="senior-lecturer">Giảng viên chính</option>
            <option value="associate-professor">Phó Giáo sư</option>
            <option value="professor">Giáo sư</option>
          </select>
        </div>
      </div>

      {/* Third Row - Four Columns: Số hộ chiếu (*), Hạn hộ chiếu, Hạn thị thực, Ngày nhập cảnh */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        {/* Số hộ chiếu */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Số hộ chiếu <span className="text-red-500">(*)</span>
          </label>
          <input
            type="text"
            value={formData.passportNumber}
            onChange={(e) =>
              handleInputChange("passportNumber", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Số hộ chiếu ..."
          />
        </div>

        {/* Hạn hộ chiếu */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hạn hộ chiếu
          </label>
          <input
            type="date"
            value={formData.passportExpiry}
            onChange={(e) =>
              handleInputChange("passportExpiry", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Hạn thị thực */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hạn thị thực
          </label>
          <input
            type="date"
            value={formData.visaExpiry}
            onChange={(e) => handleInputChange("visaExpiry", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Ngày nhập cảnh */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ngày nhập cảnh
          </label>
          <input
            type="date"
            value={formData.entryDate}
            onChange={(e) => handleInputChange("entryDate", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Fourth Row - Five Columns: Địa chỉ tạm trú tại VN, Quốc tịch (*), Chọn Quốc gia, Tổ chức, (trống) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        {/* Địa chỉ tạm trú tại VN - spans 2 columns */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Địa chỉ tạm trú tại VN
          </label>
          <input
            type="text"
            value={formData.temporaryAddress}
            onChange={(e) =>
              handleInputChange("temporaryAddress", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Địa chỉ tạm trú tại VN..."
          />
        </div>

        {/* Quốc tịch */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quốc tịch <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.nationality}
            onChange={(e) => handleInputChange("nationality", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Quốc tịch -</option>
            <option value="vietnam">Việt Nam</option>
            <option value="usa">Hoa Kỳ</option>
            <option value="japan">Nhật Bản</option>
            <option value="south-korea">Hàn Quốc</option>
            <option value="singapore">Singapore</option>
            <option value="thailand">Thái Lan</option>
            <option value="malaysia">Malaysia</option>
            <option value="philippines">Philippines</option>
            <option value="indonesia">Indonesia</option>
            <option value="china">Trung Quốc</option>
            <option value="australia">Úc</option>
            <option value="germany">Đức</option>
            <option value="france">Pháp</option>
            <option value="uk">Anh</option>
            <option value="canada">Canada</option>
          </select>
        </div>

        {/* Chọn Quốc gia */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Chọn Quốc gia
          </label>
          <select
            value={formData.country}
            onChange={(e) => handleInputChange("country", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Chọn Quốc gia -</option>
            <option value="vietnam">Việt Nam</option>
            <option value="usa">Hoa Kỳ</option>
            <option value="japan">Nhật Bản</option>
            <option value="south-korea">Hàn Quốc</option>
            <option value="singapore">Singapore</option>
            <option value="thailand">Thái Lan</option>
            <option value="malaysia">Malaysia</option>
            <option value="philippines">Philippines</option>
            <option value="indonesia">Indonesia</option>
            <option value="china">Trung Quốc</option>
            <option value="australia">Úc</option>
            <option value="germany">Đức</option>
            <option value="france">Pháp</option>
            <option value="uk">Anh</option>
            <option value="canada">Canada</option>
          </select>
        </div>

        {/* Tổ chức */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tổ chức
          </label>
          <div className="flex items-center space-x-2">
            <select
              value={formData.organization}
              onChange={(e) =>
                handleInputChange("organization", e.target.value)
              }
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">- Tổ chức -</option>
              <option value="university">Trường Đại học</option>
              <option value="research-institute">Viện Nghiên cứu</option>
              <option value="government-agency">Cơ quan Chính phủ</option>
              <option value="ngo">Tổ chức Phi chính phủ</option>
              <option value="enterprise">Doanh nghiệp</option>
              <option value="international-organization">
                Tổ chức Quốc tế
              </option>
            </select>
            <button
              type="button"
              onClick={() => navigate("/add-new-organization")}
              className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 transition-colors"
              title="Thêm tổ chức mới"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Empty space for alignment */}
        <div></div>
      </div>
    </div>
  );
};

export default ForeignStaffBasicInfo;
